import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button, Card, Row, Col, Collapse, Form } from "react-bootstrap";
import Select, { components } from 'react-select';

import { loadEnterpriseAssociationsStart } from "services/redux/actions/serviceDesk";

import Icon from "components/Icons/Icon";
import { Tooltip } from "components/Common/Tooltip/Tooltip";
import Alert from "components/Common/Alert";
import conxtdOut from "apis/conxtdOut";

import Axios from "../../../../../../node_modules/axios/index";

import { darkModeSelectStyles } from "components/Common/CustomStyles/darkModeSelectStyles";

import { isEmpty, isEqual, _ } from "lodash";

import Group from "./Group";

import "./SingleAssociation.scss"
import GroupsSelect from "./GroupsSelect";

function SingularAssociation({
  enterpriseName,
  enterprise_id,
  group_ids,
  groups,
  siteName,
  customRef,
  billingStatus,
  sudoSiteId,
  enterpriseSiteId,
  enterpriseLogo,
  windowSize,
  editMode,
  alternateEditStyle,
  setInitialiseFormDataTrigger,
  initialiseFormDataTrigger,
  initialFormData,
  setInitialFormData,
  editData,
  setEditData,
  changeDetected,
  setChangeDetected,
  index,
  setTooltipDescription,
  setInvalidEnterprise,
  addToEnterprise,
}) {

  const CustomGroupsOption = (props) => {
    const { data } = props;
    const { isParent, isLinked } = data;

    return (
      <components.Option 
        {...props} 
        onClick={() => {return}}
        className={`${isParent || isLinked ? "option-disabled" : ""}`}
      >
        <div className="d-flex align-items-center pl-1 pr-1">
          <span>{data.label}</span>
        </div>
      </components.Option>
    );
  };
  
  const dispatch = useDispatch();

  const { darkMode } = useSelector((state) => state.app);
  const siteRef = useSelector((state) => state.serviceDesk.siteRef);
  const associations = useSelector((state) => state.serviceDesk.enterpriseAssociations);

  // validations/checks
  const [dataTypesAligned, setDataTypesAligned] = useState(false);

  // form options
  const [groupsFormData, setGroupsFormData] = useState([]);
  const [billingFormData, setBillingFormData] = useState([]);
  
  // current populated form values
  const [groupValues, setGroupValues] = useState([]);
  const [customRefValue, setCustomRefValue] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [billingStatusValue, setBillingStatusValue] = useState([]);

  // determines if a user has authority to edit this SingularAssociation row (aka an Enterprise Site)
  const [editAuthorised, setEditAuthorised] = useState(false);

  useEffect(() => {
    setEditData(prev => {
      const updated = new Map(prev);

      const association = updated.get(enterprise_id);
      if (!association) return prev;

      updated.set(enterprise_id, {
        ...association,
        name: nameValue,
        billing_status: billingStatusValue,
        custom_site_ref: customRefValue,
        groups: groupValues,
      });

      return updated;
    })
  }, [initialFormData, nameValue, billingStatusValue, customRefValue, groupValues])

  // group hover logic ------>
  const [currentGroupId, setCurrentGroupId] = useState(null);
  const [groupHover, setGroupHover] = useState(false);

  useEffect(() => {
    if (groupHover && currentGroupId) {
      return;
    }

    !groupHover && setCurrentGroupId(null);
  }, [groupHover, currentGroupId])

  const handleShowGroupOnHover = () => {
    setGroupHover(true);
  }
  
  const handleHideGroupOnHover = () => {
    setGroupHover(false);
  }
  
  const handleCurrentGroupId = (id) => {
    setCurrentGroupId(id ?? null);
  }
  // -------------------------|

  useEffect(() => {
    const source = Axios.CancelToken.source();
    const enterpriseIds = associations?.map(association => association?.enterprise?.id)

    if (editAuthorised && !alternateEditStyle) {
      const getEditData = conxtdOut.get(`/ServiceDesk/getEditAssociationFormData/${enterpriseIds}`, { cancelToken: source.token });
      getEditData.then((response) => {
        const data = response?.data;
        setBillingFormData(data?.billingData && enterprise_id ? 
          Object.entries(data?.billingData)
          .map(([name, id]) => ({ label: name, value: id }))
        : 
          []
        );
      }).catch((error) => {
        console.log(error) 
      })
    }

    return () => {
      source.cancel("Component unmounted: request canceled");
    }
  }, [associations, editAuthorised, alternateEditStyle, initialFormData])

  const initialiseFormData = () => {
    setGroupsFormData([]);
    setBillingFormData([]);
    setGroupValues([]);
    setCustomRefValue([]);
    setNameValue("");
    setBillingStatusValue([]);
    setInitialFormData([]);
    setEditData([]);
  }

  useEffect(() => {
    if (initialiseFormDataTrigger) {
      initialiseFormData();
      setInitialiseFormDataTrigger(false);
    }
  }, [initialiseFormDataTrigger])

  useEffect(() => {
  if (addToEnterprise) return;

  if (editAuthorised) {
    setInitialFormData(prev =>
      isEmpty(prev)
        ? associations.reduce((acc, association) => {
            acc.set(association.enterprise.id, {
              enterprise: association.enterprise.id,
              enterprise_site_id: association.id,
              name: association.name,
              billing_status: association.billing_status,
              custom_site_ref: association.custom_site_id,
              groups: Object.entries(association.enterprise_groups)
                .slice(1)
                .filter(([key, value]) => value?.isRoot || !value?.isParent)
                .map(([key, value]) => {
                  const { name, id, isParent, isRoot } = value;
                  return (
                    {
                      label: name,
                      value: id,
                      isParent: isParent,
                      isRoot: isRoot,
                      unlink: false,
                    }
                  )
                })
            });
            return acc;
          }, new Map())
        : prev
    );
  }
}, [associations, addToEnterprise, editAuthorised]);

  useEffect(() => {
    if (!addToEnterprise) {
      const nameLengthInvalid = nameValue.length < 1;
      setInvalidEnterprise(!changeDetected || nameLengthInvalid)
      setTooltipDescription( 
        <ul className="m-0 pl-3 text-left">
          {nameLengthInvalid ? <li key={1}>site name is required</li> : null}
          {!changeDetected ? <li key={2}>no changes have been made</li> : null}
        </ul> 
      )
    }
  }, [nameValue, changeDetected, addToEnterprise])

  useEffect(() => {
    if (!isEmpty(initialFormData) && isEmpty(editData)) {
      setEditData(initialFormData);
    }
  }, [initialFormData])

  useEffect(() => {
    if (!isEmpty(editData)) {
      const association = editData.get(enterprise_id) ?? "";
      setGroupValues(association?.groups ?? [])
      setCustomRefValue(association?.custom_site_ref ?? "")
      setNameValue(association?.name ?? "")
      setBillingStatusValue(association?.billing_status ?? {label: "", value: ""})
    }
  }, [editData])

  useEffect(() => {
    setDataTypesAligned(Object.prototype.toString.call(initialFormData) === Object.prototype.toString.call(editData));
  }, [initialFormData, editData])

  useEffect(() => {
    if (dataTypesAligned) {
      setChangeDetected(!isEqual(initialFormData, editData))
    }
  }, [initialFormData, editData, dataTypesAligned])

  const handleUnlinkAssociation = () => {
    Alert({
      html: `<div>Are you sure you want to unlink "<span class="font-weight-bolder">${siteName}</span>" from "<span class="font-weight-bolder">${enterpriseName}</span>"?</div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Confirm",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        const unlinkEnterpriseAssociation = conxtdOut.post(`/ServiceDesk/unlinkEnterpriseAssociation/${enterpriseSiteId}`);
          unlinkEnterpriseAssociation.then(() => {
            Alert({
              text: "Site unlinked successfully",
              icon: "success",
              timerProgressBar: true,
              timer: 5000,
            });
            if (sudoSiteId) {
              dispatch(loadEnterpriseAssociationsStart({
                sudoSiteId: sudoSiteId,
              }))
            }
            
            if (enterprise_id) {
              window.caching.flush(`LOAD_GROUPS_BACKGROUND_SUCCESS/${enterprise_id}`);
              window.caching.flush(`LOAD_SITES_BACKGROUND_SUCCESS/${enterprise_id}`);
            }

            setInitialiseFormDataTrigger(true);
          }).catch((error) => {
            Alert({
              text: "An issue occurred. Please try again later.",
              icon: "error",
            });
          })
      } else {
        return
      }
    })
  }

  return (
    <>
      <Card className={`singular-association m-0 pl-2 pr-3 ${darkMode ? "darkMode" : ""} ${editMode ? editAuthorised ? "" : "disabled" : ""}`} data-intercom-target={"individual-enterprise-association-container"}>
          <Row className='d-flex justify-content-between align-items-center flex-nowrap' id='enterprise-associations-groups'>
              {/* Enterprise Name */}
              <Col sm={4} md={editMode && !alternateEditStyle ? 2 : 3} lg={editMode && !alternateEditStyle ? 2 : 3} className={`enterprise-associations-group hide-scrollbar ${windowSize?.width < 500 && enterpriseLogo ? "thumbnail-logo" : ""}`}>
                {windowSize?.width < 500 && enterpriseLogo ?
                  <img src={enterpriseLogo} alt={`'${enterpriseName}' logo`} style={{maxWidth: "25px"}}></img>
                :
                  <div
                    data-intercom-target={"enterprise-association-ent-name"}
                    name="ent_associations_header"
                    type="text"
                    className="enterprise-associations-text"
                    onChange={() => {return}}
                  >{enterpriseName ?? ""}</div>
                }
              </Col>

              {/* Site Name */}
              {editAuthorised && !alternateEditStyle && editData ?
                <Col md={!alternateEditStyle ? 2 : 3} lg={!alternateEditStyle ? 2 : 3} className="hide-scrollbar detail-field py-0 px-2 mb-0">
                  <Form.Control
                    key={index + "_name"}
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value ?? "")}
                    className={`${editAuthorised ? "" : "edit-mode"} enterprise-associations-group`}
                  />
                </Col>
              :
                <Col md={editMode && !alternateEditStyle ? 2 : 3} lg={editMode && !alternateEditStyle ? 2 : 3} className="hide-scrollbar">
                  <div
                    data-intercom-target={"enterprise-association-site-name"}
                    name="site_name"
                    type="text"
                    className="enterprise-associations-text enterprise-associations-group"
                    onChange={() => {return}}
                  >{siteName ?? ""}</div>
                </Col>
              }

              {windowSize?.width > 700 ?
                <>
                  {/* Custom Ref */}
                  {editAuthorised && !alternateEditStyle && editData ?
                    <Col md={editAuthorised && !alternateEditStyle ? 2 : 3} lg={editAuthorised && !alternateEditStyle ? 2 : 3} className="hide-scrollbar detail-field py-0 px-2 mb-0">
                      <Form.Control
                        key={index + "_custom_site_id"}
                        value={customRefValue}
                        onChange={(e) => setCustomRefValue(e.target.value)}
                        className={`${editAuthorised ? "" : "edit-mode"} enterprise-associations-group`}
                      />
                    </Col>
                  :
                    <Col md={2} lg={2} className="hide-scrollbar">
                      <div
                        data-intercom-target={"enterprise-association-custom-ref"}
                        name="custom_ref"
                        type="text"
                        className="enterprise-associations-text enterprise-associations-group"
                      >{customRef ?? ""}</div>
                    </Col>
                  }

                  {/* Groups */}
                    <Col 
                      md={editAuthorised && !alternateEditStyle ? 2 : 3} 
                      lg={editAuthorised && !alternateEditStyle ? 2 : 3} 
                      className={editMode ? "detail-field py-0 mb-0" : "enterprise-associations-group hide-scrollbar w-100"}
                    >
                      {enterprise_id &&
                        <GroupsSelect
                          groups={groups}
                          groupsFormData={groupsFormData}
                          groupValues={groupValues}
                          enterpriseId={enterprise_id}
                          enterpriseIds={associations?.map(association => association?.enterprise?.id)}
                          setGroupValues={setGroupValues}
                          setGroupsFormData={setGroupsFormData}
                          editing={true}
                          editMode={editMode}
                          editAuthorised={editAuthorised}
                          setEditAuthorised={setEditAuthorised}
                          alternateEditStyle={false}
                          initialFormData={initialFormData}
                          editData={editData}
                          hidden={!editMode || alternateEditStyle}
                        />
                      }
                      {(!editMode || alternateEditStyle) && groups?.map((group, index) => {
                        // does not render root group if sub groups exist 
                        const numOfGroups = Array.isArray(groups) ? groups.length : null;
                        if (numOfGroups > 1 && group.isRoot) {
                          return;
                        }
                        
                        return (
                          <Group
                            key={index}
                            groupDetails={group ?? {}}
                            enterpriseId={enterprise_id ?? null}
                            handleShowGroupOnHover={handleShowGroupOnHover}
                            handleHideGroupOnHover={handleHideGroupOnHover}
                            handleCurrentGroupId={handleCurrentGroupId}
                            index={index}
                            isFocused={groupHover}
                            numOfGroups={numOfGroups}
                          />
                        )
                        })}
                    </Col>

                  {/* Billing Status */}
                  {editAuthorised && !alternateEditStyle ?
                    <Col 
                      md={editMode && !alternateEditStyle ? 2 : 3} 
                      lg={editMode && !alternateEditStyle ? 2 : 3} 
                      className="detail-field py-0 px-2 mb-0"
                    >
                      <Select
                        menuPosition="fixed"
                        data-intercom-target={"add-to-enterprise-billing-status-select-edit"}
                        name="billing_status_edit"
                        className={`w-100 ${editAuthorised ? "" : "edit-mode"}`}
                        classNamePrefix="site-editor"
                        options={billingFormData}
                        value={{ label: billingStatusValue, value: billingStatusValue }}
                        maxMenuHeight={400}
                        onChange={(e) => setBillingStatusValue(e.value)}
                        styles={darkMode ? darkModeSelectStyles : ""}
                      />
                    </Col>
                  :
                    <Col md={2} lg={2} className="enterprise-associations-group hide-scrollbar">
                      <div
                        data-intercom-target={"enterprise-association-billing-status"}
                        name="billing_status"
                        type="text"
                        onChange={() => {return}}
                      >{billingStatus ?? ""}</div>
                    </Col>
                  }
                </>
              :
                <></>
              }

              {/* Actions */}
              <Collapse in={(editMode && !alternateEditStyle)}>
                  <Col md={2} lg={1} className={`enterprise-associations-group hide-scrollbar actions actions-collapse-container justify-content-center`}>
                    {editAuthorised ?
                      <span>
                        <Link
                          to={`/enterprise/${enterprise_id && enterprise_id}/${(Array.isArray(group_ids) && group_ids.length > 0) ? group_ids.find(group => group.isRoot)?.id : null}/view-group?site-ref=${siteRef ?? null}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Tooltip description="Go to Enterprise" placement="bottom">
                          <Button data-intercom-target={"enterprise-association-edit-button"} className="action-button-enterprise-associations">
                            <Icon className="fas fa-edit" />
                          </Button>
                          </Tooltip>
                        </Link>
                        <Tooltip description="Unlink All" placement="bottom">
                          <Button onClick={handleUnlinkAssociation} data-intercom-target={"enterprise-association-delete-button"} className="action-button-enterprise-associations remove-ent-button">
                            <Icon className="fas fa-unlink" />
                          </Button>
                        </Tooltip>
                      </span>
                      :
                      <span>
                        <Tooltip description="You are not authorised to make changes to this Enterprise Site" placement="bottom">
                          <Icon className="fas fa-ban fa-3x"/>
                        </Tooltip>
                      </span>
                    }
                  </Col>
              </Collapse>
          </Row>
      </Card>      
    </>
  )
}

export default SingularAssociation
